import { MantineProvider } from '@mantine/core';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import '@mantine/core/styles.css';
import './index.css';

import { ApiError } from './api/client';
import { SESSION_QUERY_KEY } from './api/queries';
import { App } from './App';
import { AuthGate } from './components/AuthGate';
import { theme } from './theme';

const STALE_TIME_MS = 15_000;

function isUnauthorized(error: Error): boolean {
  return error instanceof ApiError && error.isUnauthorized;
}

/**
 * A session can run out between two requests, so any 401 rechecks it: the gate
 * then swaps the ledger for the login form instead of showing an error.
 */
const queryCache = new QueryCache({
  onError: (error) => {
    if (isUnauthorized(error)) {
      void queryClient.invalidateQueries({ queryKey: [SESSION_QUERY_KEY] });
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_MS,
      // Retrying a 401 only delays the login form.
      retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 1,
      refetchOnWindowFocus: true,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <MantineProvider theme={theme} forceColorScheme="dark">
      <QueryClientProvider client={queryClient}>
        <AuthGate>
          <App />
        </AuthGate>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
